import "./AccountantDispatch.scss";

import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FiSend } from "react-icons/fi";

import Card from "../../../components/Card/Card";
import Button from "../../../components/Utils/Button/Button";
import DynamicTable from "../../../components/Utils/DynamicTable/DynamicTable";
import Spinner from "../../../components/Utils/Spinner/Spinner";
import useLoadingStore from "../../../stores/LoadingStore";
import {
  AccountantDispatchesResponse,
  AccountantDocument,
  AccountingDocumentType,
  DispatchStatus,
} from "../../../types/AccountantDispatch";
import { QuarterKey, quarterOptions } from "../../../types/Quarter";
import { formatCurrency } from "../../../Utils/formatCurrency";
import appFetch from "../../../Utils/Services/Fetch/appFetch";

// While a document is being sent the list refreshes itself at this pace.
const POLL_INTERVAL_MS = 3000;

const DOCUMENT_TYPE_LABELS: Record<AccountingDocumentType, string> = {
  expense: "Dépense",
  revenue: "Rentrée",
  invoice: "Facture",
};

type StatusKey = DispatchStatus | "unsent" | "noFile";

const STATUS_LABELS: Record<StatusKey, string> = {
  delivered: "Délivré",
  sent: "Envoyé",
  sending: "Envoi en cours",
  failed: "Échec",
  unsent: "Non envoyé",
  noFile: "Pas de fichier",
};

const STATUS_HINTS: Record<StatusKey, string> = {
  delivered: "Le serveur du comptable a accepté l'e-mail",
  sent: "E-mail parti, en attente de la confirmation de livraison",
  sending: "Envoi en cours",
  failed: "L'e-mail n'a pas pu être délivré",
  unsent: "Ce document n'a pas encore été envoyé au comptable",
  noFile: "Aucun fichier n'est joint à ce document",
};

const getStatusKey = (document: AccountantDocument): StatusKey => {
  if (document.status) {
    return document.status;
  }
  return document.has_file ? "unsent" : "noFile";
};

const isPending = (document: AccountantDocument): boolean =>
  document.has_file &&
  (document.status === null || document.status === "failed");

const currentQuarterKey = (): QuarterKey =>
  `Q${Math.ceil((new Date().getMonth() + 1) / 3)}` as QuarterKey;

const formatDate = (value: string | null): string =>
  value
    ? new Date(value).toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      })
    : "-";

const formatDateTime = (value: string | null): string =>
  value
    ? new Date(value).toLocaleString("fr-FR", {
        day: "numeric",
        month: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const documentKey = (document: AccountantDocument): string =>
  `${document.document_type}${document.document_id}`;

const AccountantDispatch = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState<QuarterKey>(currentQuarterKey());
  const [data, setData] = useState<AccountantDispatchesResponse | null>(null);
  const { loadingStates, setLoading } = useLoadingStore();

  const fetchData = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoading("accountantDispatches", true);
      }
      const response = await appFetch(
        "/api/accounting/accountant/getDispatches",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ year, quarter }),
        },
      );
      if (response && response.documents) {
        setData(response);
      }
      if (!silent) {
        setLoading("accountantDispatches", false);
      }
    },
    [year, quarter, setLoading],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const documents = useMemo(() => data?.documents ?? [], [data]);
  const isSending = documents.some((item) => item.status === "sending");

  useEffect(() => {
    if (!isSending) {
      return undefined;
    }
    const timer = setTimeout(() => fetchData(true), POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [isSending, documents, fetchData]);

  const counts = useMemo(() => {
    const result: Record<StatusKey, number> = {
      delivered: 0,
      sent: 0,
      sending: 0,
      failed: 0,
      unsent: 0,
      noFile: 0,
    };
    documents.forEach((item) => {
      result[getStatusKey(item)] += 1;
    });
    return result;
  }, [documents]);

  const pendingCount = documents.filter(isPending).length;
  const accountantEmail = data?.settings.accountant_email;

  const sendQuarter = async () => {
    setLoading("accountantSendQuarter", true);
    const response = await appFetch("/api/accounting/accountant/sendQuarter", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ year, quarter }),
    });
    if (response && response.message) {
      toast.success(response.message);
      await fetchData(true);
    }
    setLoading("accountantSendQuarter", false);
  };

  const sendDocument = async (document: AccountantDocument) => {
    const loadingKey = `accountantSend${documentKey(document)}`;
    setLoading(loadingKey, true);
    const response = await appFetch("/api/accounting/accountant/send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        document_type: document.document_type,
        document_id: document.document_id,
      }),
    });
    if (response && response.status === "failed") {
      toast.error(response.message);
    } else if (response && response.message) {
      toast.success(response.message);
    }
    await fetchData(true);
    setLoading(loadingKey, false);
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "document_type",
      render: (value: AccountingDocumentType) => DOCUMENT_TYPE_LABELS[value],
    },
    {
      title: "Date",
      dataIndex: "document_date",
      render: (value: string) => formatDate(value),
    },
    {
      title: "Nom",
      dataIndex: "label",
    },
    {
      title: "Montant",
      dataIndex: "amount",
      render: (value: string | null) =>
        value === null ? "-" : `${formatCurrency(Number(value))} €`,
    },
    {
      title: "Statut",
      dataIndex: "status",
      render: (_: DispatchStatus | null, record: AccountantDocument) => {
        const statusKey = getStatusKey(record);
        return (
          <div className="accountantDispatchStatus">
            <span
              className={`accountantDispatchBadge accountantDispatchBadge-${statusKey}`}
              title={record.error ?? STATUS_HINTS[statusKey]}
            >
              {STATUS_LABELS[statusKey]}
            </span>
            {record.error && (
              <span className="accountantDispatchError">{record.error}</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Dernier envoi",
      dataIndex: "sent_at",
      render: (_: string | null, record: AccountantDocument) =>
        formatDateTime(record.delivered_at ?? record.sent_at),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: string, record: AccountantDocument) =>
        record.has_file ? (
          <Button
            className="accountantDispatchSendButton"
            onClick={() => sendDocument(record)}
            label={record.status ? "Renvoyer" : "Envoyer"}
            icon={<FiSend />}
            isLoading={loadingStates[`accountantSend${documentKey(record)}`]}
            disabled={!accountantEmail || record.status === "sending"}
          />
        ) : (
          "-"
        ),
    },
  ];

  return (
    <Card className="accountantDispatch">
      <div className="accountantDispatchHeader">
        <div className="accountantDispatchHeading">
          <h3 className="accountantDispatchTitle">Envoi au comptable</h3>
          {data &&
            (accountantEmail ? (
              <p className="accountantDispatchSubtitle">
                Destinataire : <strong>{accountantEmail}</strong> · Envoi
                automatique{" "}
                {data.settings.accountant_forwarding_enabled
                  ? "activé"
                  : "désactivé"}
              </p>
            ) : (
              <p className="accountantDispatchSubtitle accountantDispatchWarning">
                Aucune adresse configurée : renseignez l&apos;email du comptable
                dans les paramètres de l&apos;organisation.
              </p>
            ))}
        </div>
        <div className="accountantDispatchYearNav">
          <button
            className="accountantDispatchYearBtn"
            onClick={() => setYear((y) => y - 1)}
            aria-label="Année précédente"
          >
            {"<"}
          </button>
          <span className="accountantDispatchYear">{year}</span>
          <button
            className="accountantDispatchYearBtn"
            onClick={() => setYear((y) => y + 1)}
            aria-label="Année suivante"
          >
            {">"}
          </button>
        </div>
      </div>

      <div className="accountantDispatchQuarters">
        {quarterOptions.map(({ key, label, period }) => (
          <button
            key={key}
            className={`accountantDispatchQuarter ${
              key === quarter ? "accountantDispatchQuarterActive" : ""
            }`}
            onClick={() => setQuarter(key)}
          >
            <span className="accountantDispatchQuarterLabel">{label}</span>
            <span className="accountantDispatchQuarterPeriod">{period}</span>
          </button>
        ))}
      </div>

      {loadingStates.accountantDispatches ? (
        <Spinner />
      ) : (
        <>
          <div className="accountantDispatchSummary">
            <div className="accountantDispatchCounts">
              {(Object.keys(STATUS_LABELS) as StatusKey[])
                .filter((key) => counts[key] > 0)
                .map((key) => (
                  <span
                    key={key}
                    className={`accountantDispatchBadge accountantDispatchBadge-${key}`}
                  >
                    {STATUS_LABELS[key]} : {counts[key]}
                  </span>
                ))}
              {documents.length === 0 && (
                <span className="accountantDispatchEmpty">
                  Aucun document sur ce trimestre
                </span>
              )}
            </div>
            <Button
              className="accountantDispatchSendQuarter"
              onClick={sendQuarter}
              label={`Envoyer les non envoyés (${pendingCount})`}
              icon={<FiSend />}
              isLoading={loadingStates.accountantSendQuarter}
              disabled={!accountantEmail || pendingCount === 0}
            />
          </div>

          {documents.length > 0 && (
            <DynamicTable
              data={documents}
              columns={columns}
              pagined
              maxRow={25}
              mobile
            />
          )}
        </>
      )}
    </Card>
  );
};

export default AccountantDispatch;
